import React, { useEffect, useState, useCallback } from 'react';
import { UserPlus, Trash2, ShieldCheck, Users, KeyRound, Copy, Check, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { key: 'admin', label: 'Менеджер (admin)' },
  { key: 'dealer', label: 'Дилер' },
  { key: 'agent', label: 'Агент' },
];

const emptyForm = {
  login: '',
  password: '',
  contact_person: '',
  company_name: '',
  city: '',
  phone: '',
  role: 'admin',
  private_tasks: false,
  hide_prices: false,
  watermark_disabled: false,
  linked_login: '',
};

// Human-readable error from backend detail text.
const prettyError = (detail, status) => {
  if (!detail) return 'Ошибка';
  if (status === 403 && detail.includes('Super-admin')) return 'Удалять аккаунты и менять пароли может только Руководитель (admin)';
  if (status === 400 && detail.includes('yourself')) return 'Нельзя удалить собственную учётную запись';
  if (status === 400 && detail.includes('super-admin')) return 'Нельзя удалить главного Руководителя';
  if (status === 404) return 'Сотрудник уже удалён или не найден';
  return detail;
};

const formatDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('ru-RU'); } catch (_) { return null; }
};

export default function StaffTab({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [pwdModal, setPwdModal] = useState(null); // { user, mode: 'reset', newPwd?, savedAt? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/dealer-auth/list-all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`${r.status}`);
      setUsers(await r.json());
    } catch (e) {
      toast.error('Не удалось загрузить сотрудников');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.login.trim() || !form.password.trim()) {
      toast.error('Логин и пароль обязательны');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        login: form.login.trim().toLowerCase(),
        password: form.password,
        contact_person: form.contact_person,
        company_name: form.company_name || undefined,
        city: form.city,
        phone: form.phone,
        role: form.role,
        private_tasks: form.private_tasks,
        hide_prices: form.hide_prices,
        watermark_disabled: form.watermark_disabled,
        linked_login: form.linked_login.trim() || undefined,
      };
      const r = await fetch(`${API_URL}/api/dealer-auth/create-linked`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Ошибка');
      toast.success(`Сотрудник ${j.action === 'created' ? 'создан' : 'обновлён'}: ${j.login}`);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.message || 'Ошибка создания');
    } finally {
      setCreating(false);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Удалить сотрудника ${u.login}? При перезапуске сервера он восстановлен не будет.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/dealer-auth/${u.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw { message: prettyError(j.detail, r.status) };
      toast.success(`Аккаунт ${u.login} удалён. При перезапуске сервера восстановлен не будет.`);
      load();
    } catch (e) {
      toast.error(e.message || 'Не удалось удалить');
    }
  };

  const openResetPwd = (u) => setPwdModal({ user: u, mode: 'reset', manualPwd: '', generated: null, saving: false, copied: false });

  const submitResetPwd = async (generate) => {
    if (!pwdModal) return;
    const body = generate ? { generate: true } : { password: pwdModal.manualPwd };
    setPwdModal(p => ({ ...p, saving: true }));
    try {
      const r = await fetch(`${API_URL}/api/dealer-auth/${pwdModal.user.id}/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw { message: prettyError(j.detail, r.status) };
      if (j.password) {
        // Generated — show once
        setPwdModal(p => ({ ...p, saving: false, generated: j.password, savedAt: j.password_changed_at }));
      } else {
        toast.success(`Пароль обновлён для ${pwdModal.user.login}`);
        setPwdModal(null);
      }
      // Reload to refresh password_changed_at column
      setUsers(prev => prev.map(x => x.id === pwdModal.user.id ? { ...x, password_changed_at: j.password_changed_at } : x));
    } catch (e) {
      setPwdModal(p => ({ ...p, saving: false }));
      toast.error(e.message || 'Не удалось сменить пароль');
    }
  };

  const copyGenerated = async () => {
    if (!pwdModal?.generated) return;
    try {
      await navigator.clipboard.writeText(pwdModal.generated);
      setPwdModal(p => ({ ...p, copied: true }));
      toast.success('Пароль скопирован');
      setTimeout(() => setPwdModal(p => p && ({ ...p, copied: false })), 2000);
    } catch (_) {
      toast.error('Не удалось скопировать');
    }
  };

  const toggleFlag = async (u, flag) => {
    const newValue = !u[flag];
    // Optimistic UI
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, [flag]: newValue } : x));
    try {
      const r = await fetch(`${API_URL}/api/dealer-auth/${u.id}/flags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [flag]: newValue }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Ошибка');
      const labels = { watermark_disabled: 'вотермарк', private_tasks: 'приватный задачник', hide_prices: 'скрытие цен' };
      toast.success(`${u.login}: ${labels[flag]} — ${newValue ? 'вкл' : 'выкл'}`);
    } catch (e) {
      // Revert
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, [flag]: !newValue } : x));
      toast.error(e.message || 'Не удалось обновить');
    }
  };

  const filtered = users.filter(u => filter === 'all' ? true : u.role === filter);

  return (
    <div className="space-y-6" data-testid="staff-tab">
      {/* Create form */}
      <div className="bg-white rounded-2xl border border-[#061d36]/10 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <UserPlus className="w-5 h-5 text-[#8b4444]" />
          <h3 className="font-semibold text-lg text-[#061d36]">Создать / обновить сотрудника</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Логин *" testid="staff-login">
            <input value={form.login} onChange={e => setForm(p => ({ ...p, login: e.target.value }))}
              placeholder="например bibigul" className="input" />
          </Field>
          <Field label="Пароль *" testid="staff-password">
            <input value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              placeholder="минимум 8 символов" className="input" />
          </Field>
          <Field label="Имя сотрудника" testid="staff-name">
            <input value={form.contact_person} onChange={e => setForm(p => ({ ...p, contact_person: e.target.value }))}
              placeholder="например Бибигуль" className="input" />
          </Field>
          <Field label="Название / компания" testid="staff-company">
            <input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
              placeholder="например Менеджер Бибигуль" className="input" />
          </Field>
          <Field label="Город" testid="staff-city">
            <input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
              placeholder="Астана" className="input" />
          </Field>
          <Field label="Телефон" testid="staff-phone">
            <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
              placeholder="+7 ..." className="input" />
          </Field>
          <Field label="Роль" testid="staff-role">
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="input">
              {ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Привязать к дилеру (общие CRM/задачи)" testid="staff-linked">
            <input value={form.linked_login} onChange={e => setForm(p => ({ ...p, linked_login: e.target.value }))}
              placeholder="логин существующего дилера (необязательно)" className="input" />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-[#061d36]" data-testid="staff-private-tasks">
            <input type="checkbox" checked={form.private_tasks}
              onChange={e => setForm(p => ({ ...p, private_tasks: e.target.checked }))}
              className="w-4 h-4 accent-[#8b4444]" />
            <ShieldCheck className="w-4 h-4 text-[#8b4444]" />
            Приватный задачник (задачи видит только сотрудник и Руководитель)
          </label>
          <label className="flex items-center gap-2 text-sm text-[#061d36]" data-testid="staff-hide-prices">
            <input type="checkbox" checked={form.hide_prices}
              onChange={e => setForm(p => ({ ...p, hide_prices: e.target.checked }))}
              className="w-4 h-4 accent-[#8b4444]" />
            Скрыть цены (только для роли Дилер)
          </label>
          <label className="flex items-center gap-2 text-sm text-[#061d36]" data-testid="staff-watermark-disabled">
            <input type="checkbox" checked={form.watermark_disabled}
              onChange={e => setForm(p => ({ ...p, watermark_disabled: e.target.checked }))}
              className="w-4 h-4 accent-[#8b4444]" />
            Без водяного знака на AI-визуализациях
          </label>
        </div>

        <div className="mt-5 flex gap-3">
          <button disabled={creating} onClick={create} data-testid="staff-submit"
            className="px-5 py-2.5 bg-[#8b4444] hover:bg-[#7a3939] disabled:opacity-50 text-white rounded-lg font-medium transition-colors">
            {creating ? 'Сохранение…' : 'Создать / обновить'}
          </button>
          <button onClick={() => setForm(emptyForm)} className="px-4 py-2.5 border border-[#061d36]/15 rounded-lg text-sm text-[#061d36] hover:bg-[#061d36]/5">
            Очистить форму
          </button>
        </div>

        <p className="mt-4 text-xs text-[#061d36]/60 leading-relaxed">
          <strong>Идемпотентно:</strong> если логин уже существует — учётные данные обновятся (пароль сбросится).
          <br />
          <strong>Приватный задачник:</strong> сотрудник видит только свои задачи; другие менеджеры их не видят; только суперадмин (admin) видит всё.
        </p>
      </div>

      {/* Users list */}
      <div className="bg-white rounded-2xl border border-[#061d36]/10 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#061d36]" />
            <h3 className="font-semibold text-lg text-[#061d36]">Сотрудники и дилеры</h3>
            <span className="text-sm text-[#061d36]/50">({filtered.length})</span>
          </div>
          <div className="flex gap-1 text-xs">
            {[['all', 'Все'], ['admin', 'Менеджмент'], ['dealer', 'Дилеры'], ['agent', 'Агенты']].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`staff-filter-${k}`}
                className={`px-3 py-1.5 rounded-lg transition-colors ${filter === k ? 'bg-[#061d36] text-white' : 'bg-[#061d36]/5 text-[#061d36]/70 hover:bg-[#061d36]/10'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="py-10 text-center text-[#061d36]/50">Загрузка…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#061d36]/10 text-xs text-[#061d36]/60 uppercase">
                  <th className="py-2 px-2">Логин</th>
                  <th className="py-2 px-2">Компания / имя</th>
                  <th className="py-2 px-2">Роль</th>
                  <th className="py-2 px-2">Флаги</th>
                  <th className="py-2 px-2 text-right">Действие</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className="border-b border-[#061d36]/5 hover:bg-[#061d36]/2" data-testid={`staff-row-${u.login}`}>
                    <td className="py-2.5 px-2 font-mono text-[#8b4444]">{u.login}</td>
                    <td className="py-2.5 px-2 text-[#061d36]">
                      <div className="font-medium">{u.company_name || '—'}</div>
                      {u.contact_person && <div className="text-xs text-[#061d36]/60">{u.contact_person}</div>}
                    </td>
                    <td className="py-2.5 px-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        u.role === 'admin' ? 'bg-[#8b4444]/10 text-[#8b4444]' :
                        u.role === 'agent' ? 'bg-blue-100 text-blue-600' :
                        'bg-[#061d36]/10 text-[#061d36]/60'
                      }`}>{u.role || 'dealer'}</span>
                    </td>
                    <td className="py-2.5 px-2 text-xs">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          onClick={() => toggleFlag(u, 'private_tasks')}
                          data-testid={`staff-toggle-private-${u.login}`}
                          title="Приватный задачник — задачи видит только этот сотрудник и Руководитель"
                          className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                            u.private_tasks
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              : 'bg-[#061d36]/5 text-[#061d36]/40 hover:bg-[#061d36]/10'
                          }`}
                        >
                          приватный
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleFlag(u, 'hide_prices')}
                          data-testid={`staff-toggle-hideprices-${u.login}`}
                          title="Скрыть цены — актуально для роли Дилер"
                          className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                            u.hide_prices
                              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              : 'bg-[#061d36]/5 text-[#061d36]/40 hover:bg-[#061d36]/10'
                          }`}
                        >
                          без цен
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleFlag(u, 'watermark_disabled')}
                          data-testid={`staff-toggle-watermark-${u.login}`}
                          title="Убрать водяной знак с AI-визуализаций"
                          className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${
                            u.watermark_disabled
                              ? 'bg-lime-100 text-lime-700 hover:bg-lime-200'
                              : 'bg-[#061d36]/5 text-[#061d36]/40 hover:bg-[#061d36]/10'
                          }`}
                        >
                          без вотермарка
                        </button>
                        {u.linked_dealer_id && (
                          <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded" title="Связан с основным дилером">связан</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {u.password_changed_at && (
                          <span className="text-[10px] text-[#061d36]/40 mr-1" title={`Пароль изменён ${formatDate(u.password_changed_at)}`}>
                            {formatDate(u.password_changed_at)}
                          </span>
                        )}
                        {u.login !== 'admin' && (
                          <button
                            onClick={() => openResetPwd(u)}
                            data-testid={`staff-reset-pwd-${u.login}`}
                            title="Сменить пароль"
                            className="text-[#061d36]/70 hover:text-[#061d36] p-1.5 rounded hover:bg-[#061d36]/5 transition-colors"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                        )}
                        {u.login !== 'admin' && (
                          <button onClick={() => del(u)} data-testid={`staff-delete-${u.login}`}
                            title="Удалить сотрудника"
                            className="text-red-600 hover:text-red-800 p-1.5 rounded hover:bg-red-50 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="5" className="py-8 text-center text-[#061d36]/40">Никого нет</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid rgba(6, 29, 54, 0.15);
          border-radius: 8px;
          font-size: 14px;
          background: white;
        }
        .input:focus { outline: none; border-color: #8b4444; }
      `}</style>

      {/* Password reset modal */}
      {pwdModal && (
        <div
          data-testid="pwd-modal"
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => !pwdModal.saving && !pwdModal.generated && setPwdModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
          >
            <button
              onClick={() => !pwdModal.saving && setPwdModal(null)}
              className="absolute top-3 right-3 text-[#061d36]/40 hover:text-[#061d36] p-1 rounded"
              aria-label="Закрыть"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-full bg-[#8b4444]/10 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-[#8b4444]" />
              </div>
              <div>
                <h3 className="font-bold text-[#061d36]">Сменить пароль</h3>
                <p className="text-xs text-[#061d36]/60">
                  Сотрудник: <span className="font-mono font-semibold">{pwdModal.user.login}</span>
                </p>
              </div>
            </div>

            {pwdModal.generated ? (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  Запишите или передайте сотруднику сейчас — <b>больше он не отобразится</b>.
                </div>
                <div className="flex items-stretch gap-2">
                  <div
                    data-testid="pwd-generated-value"
                    className="flex-1 font-mono text-base bg-[#061d36]/5 border border-[#061d36]/15 rounded-lg px-3 py-2 break-all select-all"
                  >
                    {pwdModal.generated}
                  </div>
                  <button
                    onClick={copyGenerated}
                    data-testid="pwd-copy-btn"
                    className="shrink-0 px-3 bg-[#061d36] text-white rounded-lg hover:bg-[#0a2f55] transition-colors flex items-center gap-1 text-sm"
                  >
                    {pwdModal.copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-xs text-[#061d36]/60">
                  Пароль изменён {formatDate(pwdModal.savedAt)}
                </div>
                <button
                  onClick={() => setPwdModal(null)}
                  data-testid="pwd-done-btn"
                  className="w-full mt-2 bg-[#8b4444] text-white rounded-lg py-2.5 font-semibold hover:bg-[#7a3838] transition-colors"
                >
                  Готово
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#061d36]/70 font-medium mb-1 block">
                    Новый пароль (мин. 8 символов, буквы + цифры)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={pwdModal.manualPwd}
                      onChange={(e) => setPwdModal(p => ({ ...p, manualPwd: e.target.value }))}
                      className="input flex-1 font-mono"
                      placeholder="Введите или сгенерируйте"
                      data-testid="pwd-manual-input"
                      disabled={pwdModal.saving}
                    />
                    <button
                      onClick={() => submitResetPwd(true)}
                      disabled={pwdModal.saving}
                      data-testid="pwd-generate-btn"
                      className="shrink-0 px-3 bg-[#061d36]/5 border border-[#061d36]/15 text-[#061d36] rounded-lg hover:bg-[#061d36]/10 transition-colors flex items-center gap-1 text-sm"
                      title="Сгенерировать безопасный пароль"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Сгенерировать
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setPwdModal(null)}
                    disabled={pwdModal.saving}
                    className="flex-1 bg-[#061d36]/5 text-[#061d36] rounded-lg py-2.5 font-medium hover:bg-[#061d36]/10 transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => submitResetPwd(false)}
                    disabled={pwdModal.saving || !pwdModal.manualPwd}
                    data-testid="pwd-save-btn"
                    className="flex-1 bg-[#8b4444] text-white rounded-lg py-2.5 font-semibold hover:bg-[#7a3838] transition-colors disabled:opacity-50"
                  >
                    {pwdModal.saving ? 'Сохранение…' : 'Сохранить'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div>
      <label className="text-xs text-[#061d36]/70 font-medium mb-1 block">{label}</label>
      <div data-testid={testid}>{children}</div>
    </div>
  );
}
